import { useState } from "react";
import Modal from "../modals/Modal";

const NAME_MAX = 40;
const LOCATION_MAX = 60;

/**
 * KioskFormModal — create or rename a kiosk (name required, location optional).
 * `onSubmit(name, location)` does the callable; this just validates + drives the
 * busy state. On success the parent closes it.
 */
const KioskFormModal = ({ mode = "add", initialName = "", initialLocation = "", onSubmit, onClose }) => {
  const [name, setName] = useState(initialName);
  const [location, setLocation] = useState(initialLocation);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const isAdd = mode === "add";

  const submit = async () => {
    if (busy) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Kiosk name is required.");
      return;
    }
    if (trimmed.length > NAME_MAX) {
      setError(`Kiosk name must be ${NAME_MAX} characters or fewer.`);
      return;
    }
    setError("");
    setBusy(true);
    try {
      await onSubmit(trimmed, location.trim());
    } catch (err) {
      setError(err?.message || "Something went wrong. Try again.");
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} busy={busy} labelledBy="kiosk-form-title" className="kiosk-modal">
        <div className="kiosk-modal-header">
          <h2 id="kiosk-form-title" className="kiosk-modal-title">
            {isAdd ? "Add a kiosk" : "Edit kiosk"}
          </h2>
          <div className="kiosk-modal-sub">
            {isAdd
              ? "Name the device and where it lives. You'll get a one-time pairing code next."
              : "Update the kiosk's name and location."}
          </div>
        </div>
        <div className="kiosk-modal-body">
          <div className="kiosk-field">
            <label className="kiosk-label" htmlFor="kiosk-name">Kiosk name</label>
            <input
              id="kiosk-name"
              className="kiosk-input"
              type="text"
              maxLength={NAME_MAX}
              placeholder="e.g. Main Gate"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <div className="kiosk-hint">Shown on the device and in this list.</div>
          </div>
          <div className="kiosk-field">
            <label className="kiosk-label" htmlFor="kiosk-location">Location <span style={{ color: "var(--ui-text-faint)" }}>(optional)</span></label>
            <input
              id="kiosk-location"
              className="kiosk-input"
              type="text"
              maxLength={LOCATION_MAX}
              placeholder="e.g. Building A — Lobby"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
            {error && <div className="kiosk-error-text">{error}</div>}
          </div>
        </div>
        <div className="kiosk-modal-footer">
          <button type="button" className="settings-btn settings-btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="settings-btn settings-btn-primary" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : isAdd ? "Generate pairing code" : "Save changes"}
          </button>
        </div>
    </Modal>
  );
};

export default KioskFormModal;
